import { createAction } from "@reduxjs/toolkit";
import {
  FETCH_ANSWER_FOR_SEEK_REQUEST,
  FETCHING_ANSWER_FOR_SEEK_SUCCEEDED,
  FETCHING_ANSWER_FOR_SEEK_FAILED,
  FETCH_SEEK_SESSIONS_REQUEST,
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
  FETCHING_SEEK_SESSIONS_FAILED,
  DELETE_SEEK_SESSION_REQUEST,
  DELETING_SEEK_SESSION_SUCCEEDED,
  DELETING_SEEK_SESSION_FAILED,
  DELETE_SEEK_SESSIONS_REQUEST,
  DELETING_SEEK_SESSIONS_SUCCEEDED,
  DELETING_SEEK_SESSIONS_FAILED,
  FETCH_SEEK_SESSION_REQUEST,
  FETCHING_SEEK_SESSION_SUCCEEDED,
  FETCHING_SEEK_SESSION_FAILED,
  FETCH_SEEK_EMAIL_RESPONSE,
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
  INVALIDATE_SEEK_EMAIL_RESPONSE,
  UPDATE_SEEK_FEEDBACK,
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
  UPDATING_SEEK_FEEDBACK_FAILED,
  FETCH_SEEK_LIMIT,
  FETCHING_SEEK_LIMIT_SUCCEEDED,
  FETCHING_SEEK_LIMIT_FAILED,
  FETCH_SEEK_PROMPTS,
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
  FETCHING_SEEK_PROMPTS_FAILED,
  INVALIDATE_SEEK_SESSION,
  TOGGLE_SEEK,
  FETCH_ASSET_SUMMARY,
  FETCHING_ASSET_SUMMARY_SUCCEEDED,
  FETCHING_ASSET_SUMMARY_FAILED,
} from "./actionTypes";
import {
  SeekAnswerRequest,
  SeekError,
  SeekAnswerResponse,
  SeekSessionsRequest,
  SeekSessionRequest,
  SeekEmailRequest,
  SeekEmailResponse,
  SeekFeedbackRequest,
  SeekAssetSummaryRequest,
  SeekAssetSummaryResponse,
  SeekAssetSummaryErrorResponse,
} from "src/types/seek";
import axios from "axios";
import { call, put, takeEvery } from "redux-saga/effects";

const apiUrl = `${process.env.REACT_APP_API_URL}`;
const SEEK_URL = `${apiUrl}/api/2.0/seek`;

export const fetchAnswerForSeek = createAction<SeekAnswerRequest>(
  FETCH_ANSWER_FOR_SEEK_REQUEST,
);
const fetchingAnswerForSeekSucceeded = createAction<{
  id: any;
  response: SeekAnswerResponse;
}>(FETCHING_ANSWER_FOR_SEEK_SUCCEEDED);
const fetchingAnswerForSeekFailed = createAction<SeekError>(
  FETCHING_ANSWER_FOR_SEEK_FAILED,
);

const fetchAnswerForSeekSaga = function* (
  action: ReturnType<typeof fetchAnswerForSeek>
): any {
  const { id, session, question, request } = action.payload;

  let data: {
    question: string;
    session?: string;
    content?: { type: string; id: string };
  } = {
    question: question,
  };

  if (session) {
    data.session = session;
  }

  if (request) {
    data = {
      ...data,
      ...request,
    };
  }

  const fetchAnswerForSeekApi = () => {
    return axios({
      method: "POST",
      url: `${SEEK_URL}`,
      data: data,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchAnswerForSeekApi);
    yield put(fetchingAnswerForSeekSucceeded({ id, response }));
  } catch (error) {
    yield put(fetchingAnswerForSeekFailed(error as SeekError));
  }
};

export const fetchSeekSessions = createAction<SeekSessionsRequest>(
  FETCH_SEEK_SESSIONS_REQUEST,
);

const fetchingSeekSessionsSucceeded = createAction(
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
);

const fetchingSeekSessionsFailed = createAction<SeekError>(
  FETCHING_SEEK_SESSIONS_FAILED,
);

const fetchSeekSessionsSaga = function* (
  action: ReturnType<typeof fetchSeekSessions>,
): any {
  const { page = 0, limit = 10 } = action.payload || {};

  const fetchSeekSessionsApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/sessions`,
      params: {
        page: page,
        limit: limit,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekSessionsApi);
    yield put(fetchingSeekSessionsSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekSessionsFailed(error as SeekError));
  }
};

export const deleteSeekSession = createAction<SeekSessionRequest>(
  DELETE_SEEK_SESSION_REQUEST,
);

const deletingSeekSessionSucceeded = createAction<SeekSessionRequest>(
  DELETING_SEEK_SESSION_SUCCEEDED,
);

const deletingSeekSessionFailed = createAction<SeekError>(
  DELETING_SEEK_SESSION_FAILED,
);

const deleteSeekSessionSaga = function* (
  action: ReturnType<typeof deleteSeekSession>,
): any {
  const { id } = action.payload;

  const deleteSeekSessionApi = () => {
    return axios({
      method: "DELETE",
      url: `${SEEK_URL}/sessions/${id}`,
    }).then((response) => response.data);
  };

  try {
    yield call(deleteSeekSessionApi);
    yield put(deletingSeekSessionSucceeded({ id }));
  } catch (error) {
    yield put(deletingSeekSessionFailed(error as SeekError));
  }
};

export const deleteSeekSessions = createAction<void>(
  DELETE_SEEK_SESSIONS_REQUEST,
);

const deletingSeekSessionsSucceeded = createAction<void>(
  DELETING_SEEK_SESSIONS_SUCCEEDED,
);

const deletingSeekSessionsFailed = createAction<SeekError>(
  DELETING_SEEK_SESSIONS_FAILED,
);

const deleteSeekSessionsSaga = function* (): any {

  const deleteSeekSessionsApi = () => {
    return axios({
      method: "DELETE",
      url: `${SEEK_URL}/sessions`,
    }).then((response) => response.data);
  };

  try {
    yield call(deleteSeekSessionsApi);
    yield put(deletingSeekSessionsSucceeded());
  } catch (error) {
    yield put(deletingSeekSessionsFailed(error as SeekError));
  }
};

export const fetchSeekSession = createAction<SeekSessionRequest>(
  FETCH_SEEK_SESSION_REQUEST,
);

const fetchingSeekSessionSucceeded = createAction<SeekAnswerResponse>(
  FETCHING_SEEK_SESSION_SUCCEEDED,
);

const fetchingSeekSessionFailed = createAction<SeekError>(
  FETCHING_SEEK_SESSION_FAILED,
);

export const invalidateSeekSession = createAction(INVALIDATE_SEEK_SESSION);

const fetchSeekSessionSaga = function* (
  action: ReturnType<typeof fetchSeekSession>,
): any {
  const { id } = action.payload;

  const fetchSeekSessionApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/sessions/${id}/chats`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekSessionApi);
    yield put(fetchingSeekSessionSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekSessionFailed(error as SeekError));
  }
};

export const fetchSeekEmailResponse = createAction<SeekEmailRequest>(
  FETCH_SEEK_EMAIL_RESPONSE,
);

const fetchingSeekEmailResponseSucceeded = createAction<SeekEmailResponse>(
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
);

const fetchingSeekEmailResponseFailed = createAction<SeekError>(
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
);

export const invalidateSeekEmailResponse = createAction(
  INVALIDATE_SEEK_EMAIL_RESPONSE,
);

const fetchSeekEmailResponseSaga = function* (
  action: ReturnType<typeof fetchSeekEmailResponse>,
): any {
  const { id, chatId } = action.payload;

  const fetchSeekEmailResponseApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/sessions/${id}/chats/${chatId}/email`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekEmailResponseApi);
    yield put(fetchingSeekEmailResponseSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekEmailResponseFailed(error as SeekError));
  }
};

export const updateSeekFeedback =
  createAction<SeekFeedbackRequest>(UPDATE_SEEK_FEEDBACK);

const updatingSeekHelpfulSucceeded = createAction<any>(
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
);

const updatingSeekHelpfulFailed = createAction<SeekError>(
  UPDATING_SEEK_FEEDBACK_FAILED,
);

const updateSeekFeedbackSaga = function* (
  action: ReturnType<typeof updateSeekFeedback>,
): any {
  const { isHelpful, chatId } = action.payload;

  const updateSeekFeedbackApi = () => {
    return axios({
      method: "PUT",
      url: `${SEEK_URL}/chats/${chatId}/feedback`,
      params: {
        isHelpful: isHelpful,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateSeekFeedbackApi);
    yield put(updatingSeekHelpfulSucceeded(response));
  } catch (error) {
    yield put(updatingSeekHelpfulFailed(error as SeekError));
  }
};

export const fetchSeekLimit = createAction(FETCH_SEEK_LIMIT);

const fetchingSeekLimitSucceeded = createAction<any>(
  FETCHING_SEEK_LIMIT_SUCCEEDED,
);

const fetchingSeekLimitFailed = createAction<SeekError>(
  FETCHING_SEEK_LIMIT_FAILED,
);

const fetchSeekLimitSaga = function* (): any {
  const fetchSeekLimitApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/limit`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekLimitApi);
    yield put(fetchingSeekLimitSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekLimitFailed(error as SeekError));
  }
};

export const fetchSeekPrompts = createAction(FETCH_SEEK_PROMPTS);

const fetchingSeekPromptsSucceeded = createAction<any>(
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
);

const fetchingSeekPromptsFailed = createAction<SeekError>(
  FETCHING_SEEK_PROMPTS_FAILED,
);

const fetchSeekPromptsSaga = function* (): any {
  const fetchSeekPromptsApi = () => {
    return axios({
      method: "GET",
      url: `${SEEK_URL}/suggestions`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSeekPromptsApi);
    yield put(fetchingSeekPromptsSucceeded(response));
  } catch (error) {
    yield put(fetchingSeekPromptsFailed(error as SeekError));
  }
};

export const fetchAssetSummary = createAction<SeekAssetSummaryRequest>(FETCH_ASSET_SUMMARY);

const fetchingAssetSummarySucceeded = createAction<{
  id: any;
  response: SeekAssetSummaryResponse;
}>(
  FETCHING_ASSET_SUMMARY_SUCCEEDED
);

const fetchingAssetSummaryFailed = createAction<SeekAssetSummaryErrorResponse>(
  FETCHING_ASSET_SUMMARY_FAILED,
);

const fetchAssetSummarySaga = function* (action: ReturnType<typeof fetchAssetSummary>): any {

  const fetchAssetSummaryApi = () => {
    return axios({
      method: "POST",
      url: `${SEEK_URL}/asset_summary`,
      data:  action.payload
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchAssetSummaryApi);
    yield put(fetchingAssetSummarySucceeded({
      id: null,
      response
    }));
  } catch (error) {
    yield put(fetchingAssetSummaryFailed(error as SeekError));
  }
};

export const toggleSeek = createAction(TOGGLE_SEEK);

export default function* seekSaga() {
  yield takeEvery(FETCH_ANSWER_FOR_SEEK_REQUEST, fetchAnswerForSeekSaga);
  yield takeEvery(FETCH_SEEK_SESSIONS_REQUEST, fetchSeekSessionsSaga);
  yield takeEvery(DELETE_SEEK_SESSION_REQUEST, deleteSeekSessionSaga);
  yield takeEvery(FETCH_SEEK_SESSION_REQUEST, fetchSeekSessionSaga);
  yield takeEvery(FETCH_SEEK_EMAIL_RESPONSE, fetchSeekEmailResponseSaga);
  yield takeEvery(UPDATE_SEEK_FEEDBACK, updateSeekFeedbackSaga);
  yield takeEvery(FETCH_SEEK_LIMIT, fetchSeekLimitSaga);
  yield takeEvery(FETCH_SEEK_PROMPTS, fetchSeekPromptsSaga);
  yield takeEvery(DELETE_SEEK_SESSIONS_REQUEST, deleteSeekSessionsSaga);
  yield takeEvery(FETCH_ASSET_SUMMARY, fetchAssetSummarySaga);
}
